/* eslint-disable react/no-danger */
/** @jsxImportSource @emotion/react */
import Box from '@mui/material/Box';
import { useTheme } from '@emotion/react';
import Text from '@Text';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getLegalStatementAction } from '../../Hooks/actionCreators';
import {
  setHandleSideEffectsOfLegalStatement,
  updateIsLegalStatementMounted,
  updateIsSuppliersListModalOpen,
  updateShouldRenderLegalStatement,
} from '../../Lib/redux/slices/legalStatement';
import {
  currentPageHasSelectAndConnectComponents,
  getLegalStatementToShow,
  getLocaleForPrivacyPolicy,
  getStateAwareLegalStatement,
  selectCustomerStateCode,
  shouldLegalStatementBeVisible,
} from './legalStatement.helpers';
import makeLegalStatementStyling from './makeLegalStatementStyling';
import { getPageNumbersWithVisibleSupplierMatches } from '../../Helpers/components';
import makeDynamicLegalStatementStyling from './makeDynamicLegalStatementStyling';
import { NewsletterConfig } from '../../Data/NewsletterBrands';
import { shouldPageTriggerPartialSubmission } from '../../Helpers/partialSubmission';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';
import { hasPhoneConstraint } from '../__helpers__/component.helpers';
import selectIsPolarisFeatureEnabled from '../../Helpers/polaris';

const getNewslettersRequiringConsentStatements = ({
  isDynamicNewsletterPresent,
  components,
}) => {
  if (isDynamicNewsletterPresent) {
    return Object.values(NewsletterConfig).map(
      (newsletterConfig) => newsletterConfig.id
    );
  }
  return Object.values(components)
    .filter(
      (component) =>
        component.component?.type === 'newsletter-signup' &&
        component.component?.additionalSettings?.brand &&
        NewsletterConfig[component.component.additionalSettings.brand]?.id
    )

    .map((component) => {
      return NewsletterConfig[component.component.additionalSettings.brand].id;
    });
};

const LegalStatement = () => {
  const { subcategoryId } = useSelector((store) => store.submission.webform);
  const { supplierId, submissionPageNumber, navigationBarButtonTextConfig } =
    useSelector((store) => store.formConfig);
  const { currentPage, totalPages, isFinalPageBeforeSubmission } = useSelector(
    (store) => store.navigation
  );
  const { enterPhase } = useSelector((store) => store.transition);
  const { isDynamicNewsletterPresent } = useSelector(
    (store) => store.newsletter
  );
  const {
    customerMatches,
    requiresLegalStatementAboveNav:
      supplierMatchesRequireLegalStatementAboveNav,
  } = useSelector((store) => store.supplierMatches);

  const components = useSelector((store) => store.components);
  const dispatch = useDispatch();
  const legalStatement = useSelector((store) => store.legalStatement);
  const {
    isSuppliersListModalOpen,
    suppliersModal,
    shouldRenderLegalStatement,
    hasStatementGraduallyDisappeared,
    statementOverride,
  } = legalStatement;
  const customerStateCode = useSelector(selectCustomerStateCode);
  const { statement, supplierLegalStatements } = getStateAwareLegalStatement({
    legalStatement,
    customerStateCode,
  });
  const { pagesWithLoaderComponent, partialSubmissionPages } = useSelector(
    (store) => store.paging
  );
  const {
    singleSupplierStatement,
    multipleSupplierStatement: multipleSupplierMatchStatement,
  } = supplierLegalStatements;

  const textExperimentName = useGetExperimentName({
    componentName: 'Text',
  });

  const captureBsaLeadsOnAbandonedFlows = useSelector((store) =>
    selectIsPolarisFeatureEnabled(store, 'captureBsaLeadsOnAbandonedFlows')
  );

  const isPartialSubmissionPage = shouldPageTriggerPartialSubmission(
    currentPage,
    partialSubmissionPages
  );

  const hasSelectAndConnect = currentPageHasSelectAndConnectComponents(
    components,
    currentPage
  );

  // Check if current page has a phone input and BSA consent should be shown here
  const currentPageHasPhoneInput = Object.values(components).some(
    (comp) =>
      comp.component.page === currentPage &&
      hasPhoneConstraint({ component: comp })
  );
  const isBsaPhonePage =
    captureBsaLeadsOnAbandonedFlows && currentPageHasPhoneInput;

  // When SelectAndConnect is present, get the custom CTA text for the current page
  // Check customCtaOverrides first, then fall back to submit button text
  // When BSA phone page, use the "next" CTA text (e.g. "Continue") instead of "submit"
  // Otherwise, use the default submit button text
  const customCtaOverrides =
    navigationBarButtonTextConfig?.customCtaOverrides || {};
  const customPageCtaText = customCtaOverrides[currentPage]?.text;
  const getCtaTextForLegalStatement = () => {
    if (isBsaPhonePage) {
      return navigationBarButtonTextConfig?.next || 'Submit';
    }
    if (hasSelectAndConnect) {
      return customPageCtaText || navigationBarButtonTextConfig?.submit || '';
    }
    return navigationBarButtonTextConfig?.submit ?? '';
  };
  const ctaTextForLegalStatement = getCtaTextForLegalStatement();

  const statementToShow = getLegalStatementToShow({
    supplierId,
    singleSupplierStatement,
    customerMatches,
    multipleSupplierMatchStatement,
    components,
    statement,
    statementOverride,
    currentPage,
    submitButtonText: ctaTextForLegalStatement,
  });

  const isSinglePageForm =
    typeof submissionPageNumber === 'number'
      ? submissionPageNumber === 0
      : totalPages === 1;
  const theme = useTheme();
  const styling = makeLegalStatementStyling({
    theme,
  });
  const dynamicInitialStyling = makeDynamicLegalStatementStyling({ theme });
  const { phoneNumberCountryCode, countryCode, languageCode, localeCode } =
    useSelector((i) => i.formConfig);
  const privacyPolicyLocale = getLocaleForPrivacyPolicy(
    phoneNumberCountryCode,
    countryCode,
    languageCode,
    localeCode
  );

  const {
    isConsentStatementAboveNavigation:
      isConsentStatementAboveNavigationFormConfig,
    isDynamicHeightEnabled,
    haveAllInitialMessagesArrived,
  } = useSelector((store) => store.optionalFormConfig);
  const isConsentStatementAboveNavigation =
    isConsentStatementAboveNavigationFormConfig ||
    supplierMatchesRequireLegalStatementAboveNav;
  const [showLegalStatement, setShowLegalStatement] = useState(false);
  const supplierMatchPages =
    getPageNumbersWithVisibleSupplierMatches(components);

  const isLoaderPage = pagesWithLoaderComponent.includes(currentPage);
  const hasSupplierMatchQuestion = supplierMatchPages.includes(currentPage);
  const handleOpen = () => {
    if (!isSuppliersListModalOpen) {
      dispatch(
        updateIsSuppliersListModalOpen({
          isSuppliersListModalOpen: true,
        })
      );
    }
  };
  const makeFooterFullyTransparent = () => {
    const { footerRef } = window?.__private__?.dynamicWidgetHeightData || {};
    const outerFooterContainer = footerRef?.current;
    if (outerFooterContainer) {
      outerFooterContainer.style.transition = '';
      outerFooterContainer.style.opacity = 0;
    }
  };

  const newsletterBrands = getNewslettersRequiringConsentStatements({
    isDynamicNewsletterPresent,
    components,
  });

  useEffect(() => {
    const shouldFetchLegalStatement =
      !statement && !!subcategoryId && !!privacyPolicyLocale;

    if (shouldFetchLegalStatement) {
      dispatch(
        getLegalStatementAction({
          subcategoryId,
          localeCode: privacyPolicyLocale,
          supplierId,
          newsletters: newsletterBrands,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statement, subcategoryId, privacyPolicyLocale]); // other arguments are currently optional

  useEffect(() => {
    /**
     * Fix issue which expands the click listener's reach for the Privacy Policy
     * & Suppliers modal buttons beyond their anchor tags within the legal statement
     *
     * TODO: Is this correct? Should this be triggering in all situations where a legal statement could be shown?
     */
    if (enterPhase === false && isFinalPageBeforeSubmission) {
      // Navigation transition to the penultimate page just finished
      const privacyPolicyAnchorTag = document.querySelector(
        "a[data-statement-anchor='privacy-policy']"
      );
      if (privacyPolicyAnchorTag) {
        privacyPolicyAnchorTag.style.display = 'table-row';
      }
      const suppliersAnchorTag = document.querySelector(
        'a.supplier-list-link.external-supplier-list-link'
      );
      if (suppliersAnchorTag) {
        suppliersAnchorTag.style.display = 'table-row';
      }
    }
  }, [enterPhase]);

  useEffect(() => {
    if (haveAllInitialMessagesArrived && isSinglePageForm) {
      // The legal statement needs to be present on all single page forms
      dispatch(
        setHandleSideEffectsOfLegalStatement({
          handleSideEffectsOfLegalStatement: true,
        })
      );
    }
  }, [haveAllInitialMessagesArrived]);

  // When BSA phone page is active, force the dynamic height system to account for the legal statement
  // Reset when navigating away so it doesn't persist on subsequent pages
  useEffect(() => {
    if (isBsaPhonePage && !shouldRenderLegalStatement) {
      dispatch(
        updateShouldRenderLegalStatement({
          shouldRenderLegalStatement: true,
        })
      );
    }
    if (
      !isBsaPhonePage &&
      captureBsaLeadsOnAbandonedFlows &&
      shouldRenderLegalStatement
    ) {
      dispatch(
        updateShouldRenderLegalStatement({
          shouldRenderLegalStatement: false,
        })
      );
    }
  }, [isBsaPhonePage, shouldRenderLegalStatement]);

  useEffect(() => {
    let isMounted = true;
    // Inform the SuppliersListModal component indirectly by dispatching this state to the store
    dispatch(
      updateIsLegalStatementMounted({
        isLegalStatementMounted: isMounted,
      })
    );

    document.addEventListener('click', (event) => {
      if (event.target.getAttribute('data-toggle') === 'modal') {
        if (isMounted) {
          handleOpen();
        }
      }
    });

    return () => {
      isMounted = false;
      // Inform the SuppliersListModal component indirectly by dispatching this state to the store
      dispatch(
        updateIsLegalStatementMounted({
          isLegalStatementMounted: isMounted,
        })
      );
    };
  }, []);

  /**
   * This handler reveals the legal statement gradually via CSS transition
   * as soon as it is mounted on the page. dynamicInitialStyling ensures that
   * the statement is invisible to users until the transition from this handler
   * and all post-transition styling updates complete.
   */
  useEffect(() => {
    // The LegalStatement has just been newly rendered
    if (showLegalStatement && isConsentStatementAboveNavigation) {
      const consentStatementSpan = document.getElementById(
        'consentStatementSpan'
      );
      const legalStatementContainer = document.querySelector(
        "[data-testid='legalStatementContainer']"
      );
      const consentStatementHeight =
        consentStatementSpan?.parentElement?.getBoundingClientRect()?.height;
      if (!consentStatementHeight) {
        return;
      }
      legalStatementContainer.style.transition =
        'margin-bottom 0.6s ease 0s, max-height 0.6s ease 0s';

      const revealExpandedLegalStatement = () => {
        legalStatementContainer.style.maxHeight = 'fit-content';
        consentStatementSpan.style.visibility = 'visible';
        legalStatementContainer.style.transition = '';
        legalStatementContainer.removeEventListener(
          'transitionend',
          revealExpandedLegalStatement
        );
      };
      legalStatementContainer.addEventListener(
        'transitionend',
        revealExpandedLegalStatement
      );

      /**
       * Kick-off the CSS transition which gradually expands the legal statement
       * container to its full height incl. margin. The UX for this is that users
       * see the nav bar gradually shifting down.
       */
      legalStatementContainer.style.maxHeight = 'fit-content';
      legalStatementContainer.style.marginBottom = theme.mvf.spacing.medium;
    }
  }, [showLegalStatement]);

  if (suppliersModal === undefined && !isBsaPhonePage) {
    return null;
  }

  const isLegalStatementVisible = shouldLegalStatementBeVisible({
    isDynamicHeightEnabled,
    hasSupplierMatchQuestion,
    shouldRenderLegalStatement,
    isConsentStatementAboveNavigation,
    hasStatementGraduallyDisappeared,
    isFinalPageBeforeSubmission,
    isPartialSubmissionPage,
    isSelectAndConnectPage: currentPageHasSelectAndConnectComponents(
      components,
      currentPage
    ),
    isBsaPhonePage,
  });

  if (showLegalStatement !== isLegalStatementVisible) {
    if (
      isDynamicHeightEnabled &&
      isLegalStatementVisible &&
      isLoaderPage &&
      !showLegalStatement
    ) {
      // GOLD-559: Hide the footer in preparation for an easing fade in animation
      makeFooterFullyTransparent();
    }
    setShowLegalStatement(isLegalStatementVisible);
  }

  return isLegalStatementVisible ? (
    <Box
      css={
        isDynamicHeightEnabled && isConsentStatementAboveNavigation
          ? dynamicInitialStyling.legalStatement
          : styling.legalStatement
      }
      data-testid="legalStatementContainer"
    >
      <Text variant="footer" experimentName={textExperimentName}>
        <span
          data-tf-element-role="consent-language"
          id="consentStatementSpan"
          dangerouslySetInnerHTML={{
            __html: statementToShow,
          }}
        />
      </Text>
    </Box>
  ) : (
    ''
  );
};

export default LegalStatement;
